import { type Client } from '@microsoft/microsoft-graph-client';
import type { EntraUser, Employee, SPListItem } from '@/models/types';
import {
  getListItems,
  createListItem,
  updateListItem,
} from './sharePointService';

export async function fetchEntraUsers(client: Client): Promise<EntraUser[]> {
  const users: EntraUser[] = [];

  let response = await client
    .api('/users')
    .select('id,displayName,mail,department,jobTitle,accountEnabled')
    .filter('accountEnabled eq true')
    .top(100)
    .get();

  while (response) {
    for (const user of response.value) {
      if (user.mail) {
        users.push(user);
      }
    }
    if (response['@odata.nextLink']) {
      response = await client.api(response['@odata.nextLink']).get();
    } else {
      break;
    }
  }

  return users;
}

export async function fetchUserManager(
  client: Client,
  userId: string,
): Promise<string | null> {
  try {
    const manager = await client
      .api(`/users/${userId}/manager`)
      .select('mail')
      .get();
    return manager.mail ?? null;
  } catch {
    return null;
  }
}

export interface SyncResult {
  created: number;
  updated: number;
  deactivated: number;
}

export async function syncUsersToSharePoint(
  client: Client,
  entraUsers: EntraUser[],
): Promise<SyncResult> {
  const existing = await getListItems<Employee>(client, 'OC_Employees');

  const existingByEntraId = new Map<string, SPListItem<Employee>>();
  for (const item of existing) {
    if (item.fields.EntraObjectId) {
      existingByEntraId.set(item.fields.EntraObjectId, item);
    }
  }

  const result: SyncResult = { created: 0, updated: 0, deactivated: 0 };
  const processedEntraIds = new Set<string>();

  for (const entraUser of entraUsers) {
    processedEntraIds.add(entraUser.id);
    const existingItem = existingByEntraId.get(entraUser.id);

    if (existingItem) {
      // Check if update needed
      const f = existingItem.fields;
      if (
        f.Title !== entraUser.displayName ||
        f.Email !== entraUser.mail ||
        f.Department !== (entraUser.department ?? '') ||
        f.JobTitle !== (entraUser.jobTitle ?? '') ||
        f.Status !== 'Active'
      ) {
        await updateListItem<Employee>(client, 'OC_Employees', existingItem.id, {
          Title: entraUser.displayName,
          Email: entraUser.mail,
          Department: entraUser.department ?? '',
          JobTitle: entraUser.jobTitle ?? '',
          Status: 'Active',
        });
        result.updated++;
      }
    } else {
      await createListItem<Employee>(client, 'OC_Employees', {
        Title: entraUser.displayName,
        Email: entraUser.mail,
        Department: entraUser.department ?? '',
        JobTitle: entraUser.jobTitle ?? '',
        EntraObjectId: entraUser.id,
        Status: 'Active',
        JoinDate: new Date().toISOString(),
        ManagerEmail: '',
      });
      result.created++;
    }
  }

  // Deactivate employees no longer in Entra
  for (const item of existing) {
    if (
      item.fields.EntraObjectId &&
      !processedEntraIds.has(item.fields.EntraObjectId) &&
      item.fields.Status === 'Active'
    ) {
      await updateListItem<Employee>(client, 'OC_Employees', item.id, {
        Status: 'Inactive',
      });
      result.deactivated++;
    }
  }

  return result;
}
